import React, { ReactNode } from 'react'

import {
  Button,
  Divider,
  Group,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
} from '@mantine/core'
import { observer } from 'mobx-react-lite'

import { FaIcon } from '#components/FaIcon'
import { useMst } from '#hooks'
import { SnapshotSummary } from '#util/ScriptoApiClient/types'

import { CurrentScriptCard } from './CurrentScriptCard'
import { headerDateStr } from './helpers'
import { useSnapshotHistoryQuery } from './useSnapshotLandData'

import styles from './SnapshotListEntries.module.css'

const DateSeparator = ({
  snap,
  previousSnap,
}: {
  snap: SnapshotSummary
  previousSnap?: SnapshotSummary
}) => {
  const snapHeader = headerDateStr(snap)

  if (previousSnap && snapHeader === headerDateStr(previousSnap)) {
    return null
  }

  return (
    <Text tt="uppercase" fw="bold" size="12" c="gray">
      {snapHeader}
    </Text>
  )
}

const NoSnapshots = () => (
  <Stack pl={10} pr={5} pt={10}>
    <Text fw="bold">No snapshots yet</Text>
    <Text>
      Snapshots let you view and compare different versions of a script to see
      what&apos;s changed.
    </Text>
    <Text>
      To create a snapshot, you&apos;ll first need to close out of this view.
      Then, from the editor, click the&nbsp;
      <span className={styles.empty}>
        <FaIcon span icon="fa-camera" size="14" />
        &nbsp;
        <Text span fw="bold">
          Snapshot
        </Text>
      </span>
      &nbsp;button on the right side of the toolbar.
    </Text>
  </Stack>
)

// TODO [2027-03-01] Check https://bugs.webkit.org/show_bug.cgi?id=244139 to see
// if we still need to force a repaint with a key
export const SnapshotListEntries = observer<{
  scriptId: string
  children(snapshot: SnapshotSummary): ReactNode
  includeCurrScript?: boolean
}>((props) => {
  const { trackSnapshotEvent: trackEvent } = useMst().view.snapshotLand

  const history = useSnapshotHistoryQuery(props.scriptId)

  const { snapshots, total } = history.data
  const loadedSnapshotCount = snapshots.length
  const hasSnapshots = loadedSnapshotCount > 0

  return (
    <ScrollArea>
      <Stack gap={10} px={10} py={10}>
        {props.includeCurrScript && (
          <>
            <CurrentScriptCard scriptId={props.scriptId} />
            <Divider />
          </>
        )}
        {snapshots.map((snapshot, idx) => {
          return (
            <React.Fragment key={snapshot.id}>
              <DateSeparator
                snap={snapshot}
                previousSnap={snapshots[idx - 1]}
              />
              {props.children(snapshot)}
            </React.Fragment>
          )
        })}
        {!hasSnapshots && <NoSnapshots />}
        {history.hasNextPage && (
          <Group justify="center" pt={10}>
            <Button
              size="xs"
              variant="subtle"
              onClick={() => {
                trackEvent('LOAD_MORE_SNAPSHOTS')
                history.fetchNextPage()
              }}
              loading={history.isFetchingNextPage}
            >
              Load More
            </Button>
          </Group>
        )}
      </Stack>
      {hasSnapshots && (
        <Stack
          className={styles.pagination}
          align="center"
          p={5}
          /* force `position: sticky` repaint in Safari */
          key={loadedSnapshotCount}
        >
          <Text c="dimmed" size="14">
            {history.hasNextPage
              ? `${loadedSnapshotCount} of ${total}`
              : `Showing all ${total}`}
          </Text>
        </Stack>
      )}
    </ScrollArea>
  )
})

export function SnapshotListEntriesSkeleton() {
  return (
    <Stack gap={10}>
      <Skeleton height={33} radius="0.25rem" mb={14} />
      <Skeleton height={12} width={50} />
      <Skeleton height={48} radius="0.25rem" />
      <Skeleton height={30} radius="0.25rem" />
      <Skeleton height={30} radius="0.25rem" />
      <Skeleton height={12} width={80} />
      <Skeleton height={30} radius="0.25rem" />
      <Skeleton height={48} radius="0.25rem" />
    </Stack>
  )
}
